<div *ngIf="viewModel$ | async as vm">

  <ng-container *ngIf="!vm.hasCrew">
    <p-messages
      [value]="[{ severity: 'info', detail: 'No crew assigned to this job\'s events.', closable: false }]"
      [enableService]="false"
    ></p-messages>
  </ng-container>

  <ng-container *ngIf="vm.hasCrew">

    <p-messages
      *ngFor="let alert of vm.alerts"
      [value]="[{ severity: alert.severity || 'warn', summary: alert.label, detail: alert.detail, closable: false }]"
      [enableService]="false"
    ></p-messages>

    <!-- Table 1: per-column checkbox assignments (no bonus) -->
    <p-table
      [value]="vm.rows"
      [rowTrackBy]="trackByUserId"
      styleClass="p-datatable-sm"
    >
      <ng-template pTemplate="header">
        <tr>
          <th>Name</th>
          <!-- #6538: manual override — the FP pays this member locally, so their crew pay is
               zeroed here and forwarded to the FP as a lump sum. Interstate-only, like the
               payment-summary table below: the reallocation only applies to OTR jobs, and the
               column would otherwise expose it to franchises that cannot use it. -->
          <th *ngIf="vm.isInterstate" class="col-pay-to-fp" pTooltip="Zero this crew member's pay and forward the amount to the Franchise Partner">
            Pay Labor to FP
          </th>
          <th
            *ngFor="let col of vm.columnSummaries; trackBy: trackByColKey"
            [class.col-over]="col.status === 'over'"
            [class.col-under]="col.status === 'under'"
            [class.col-disabled]="col.disabled"
          >
            {{ col.label }}
          </th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-row>
        <tr>
          <td class="name-col">{{ row.name }}</td>
          <td *ngIf="vm.isInterstate" class="col-pay-to-fp">
            <div class="crew-pay-cell">
              <!-- #6620: locked when the job type leaves no Franchise Partner to forward to. -->
              <p-checkbox
                [ngModel]="row.payToFp"
                [binary]="true"
                [disabled]="vm.payToFpLocked"
                (onChange)="togglePayToFp(row.id, !row.payToFp)"
              ></p-checkbox>
            </div>
          </td>
          <td *ngFor="let col of row.columns; trackBy: trackByColKey">
            <div class="crew-pay-cell">
              <ng-container *ngIf="col.disabled; else activeCol">
                <span class="col-na">—</span>
              </ng-container>
              <ng-template #activeCol>
                <!-- #6538: locked while the member is paid via the FP, so the assignment can't be
                     changed into a double payout. The value itself is preserved, so unticking
                     "Pay Labor to FP" restores the original selection. -->
                <p-checkbox
                  [ngModel]="col.enabled"
                  [binary]="true"
                  [disabled]="row.laborPaidElsewhere"
                  (onChange)="store.dispatch(quickJsActions.inputChanged({ key: col.enabledKey, value: !col.enabled }))"
                ></p-checkbox>
                <span *ngIf="col.enabled && vm.isInterstate && !row.laborPaidElsewhere" class="col-value">
                  {{ col.defaultValue | currency: vm.currency }}
                </span>
              </ng-template>
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>

    <!-- Table 2 (payment summary) + its divider are interstate-only: they carry the
         monetary columns non-interstate users must not see. -->
    <ng-container *ngIf="vm.isInterstate">
    <hr class="crew-table-divider" />

    <!-- Table 2: payment summary -->
    <div class="pay-summary-table">
    <p-table
      [value]="vm.rows"
      [rowTrackBy]="trackByUserId"
      styleClass="p-datatable-sm"
    >
      <ng-template pTemplate="header">
        <tr>
          <th>Name</th>
          <th>Total fees for services</th>
          <th>Bonus</th>
          <th [class.col-disabled]="vm.tipsDisabled">Tips</th>
          <th>Total</th>
          <th>Paid</th>
          <th>Unpaid</th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-row>
        <tr>
          <td class="name-col">{{ row.name }}</td>

          <!-- Total fees for services (overridable) -->
          <!-- #6538: fees and bonus are locked while the member is paid via the FP, matching the
               Table 1 assignments — together they determine how much is forwarded, so they are set
               BEFORE the override is switched on; untick it to change the amount. Tips and Paid stay
               editable: tips come from the job-level pool rather than the labor buckets, so they are
               still owed to the member and their payment still needs recording. -->
          <td>
            <div class="p-inputgroup">
              <p-inputNumber
                #feesInput
                mode="currency"
                [currency]="vm.currency"
                [locale]="vm.currency === 'CAD' ? 'en-CA' : 'en-US'"
                [ngModel]="row.feesOverride"
                [placeholder]="feesInput.formatValue(row.computedFees)"
                [minFractionDigits]="2"
                [maxFractionDigits]="2"
                [disabled]="row.laborPaidElsewhere"
                (ngModelChange)="onFeesInput(row.feesOverrideKey, $event)"
                (onBlur)="commitFees(row.feesOverrideKey)"
              ></p-inputNumber>
              <button
                *ngIf="row.feesHasOverride && !row.laborPaidElsewhere"
                pButton
                type="button"
                icon="pi pi-replay"
                outlined="true"
                pTooltip="Reset"
                tabindex="-1"
                (click)="resetFees(row.feesOverrideKey)"
              ></button>
            </div>
          </td>

          <!-- Bonus (checkbox, like Table 1) -->
          <td>
            <div class="crew-pay-cell">
              <ng-container *ngIf="row.bonusCol.disabled; else bonusActive">
                <span class="col-na">—</span>
              </ng-container>
              <ng-template #bonusActive>
                <p-checkbox
                  [ngModel]="row.bonusCol.enabled"
                  [binary]="true"
                  [disabled]="row.laborPaidElsewhere"
                  (onChange)="store.dispatch(quickJsActions.inputChanged({ key: row.bonusCol.enabledKey, value: !row.bonusCol.enabled }))"
                ></p-checkbox>
                <span *ngIf="row.bonusCol.enabled && !row.laborPaidElsewhere" class="col-value">
                  {{ row.bonusCol.defaultValue | currency: vm.currency }}
                </span>
              </ng-template>
            </div>
          </td>

          <!-- Tips (overridable) -->
          <td>
            <ng-container *ngIf="vm.tipsDisabled; else tipActive">
              <span class="col-na">—</span>
            </ng-container>
            <ng-template #tipActive>
              <div class="p-inputgroup">
                <p-inputNumber
                  #tipInput
                  mode="currency"
                  [currency]="vm.currency"
                  [locale]="vm.currency === 'CAD' ? 'en-CA' : 'en-US'"
                  [ngModel]="row.tipOverride"
                  [placeholder]="tipInput.formatValue(row.computedTip)"
                  [minFractionDigits]="2"
                  [maxFractionDigits]="2"
                  (ngModelChange)="onTipInput(row.tipOverrideKey, $event)"
                  (onBlur)="commitTip(row.tipOverrideKey)"
                ></p-inputNumber>
                <button
                  *ngIf="row.tipHasOverride"
                  pButton
                  type="button"
                  icon="pi pi-replay"
                  outlined="true"
                  pTooltip="Reset"
                  tabindex="-1"
                  (click)="resetTip(row.tipOverrideKey)"
                ></button>
              </div>
            </ng-template>
          </td>

          <!-- Total (read-only) -->
          <td>
            <span>{{ row.displayTotal | currency: vm.currency }}</span>
          </td>

          <!-- Paid (overridable) -->
          <td>
            <div class="p-inputgroup">
              <p-inputNumber
                #paidInput
                mode="currency"
                [currency]="vm.currency"
                [locale]="vm.currency === 'CAD' ? 'en-CA' : 'en-US'"
                [ngModel]="row.paidAmount"
                [placeholder]="paidInput.formatValue(0)"
                [minFractionDigits]="2"
                [maxFractionDigits]="2"
                (ngModelChange)="onPaidInput(row.paidKey, $event)"
                (onBlur)="commitPaid(row.paidKey)"
              ></p-inputNumber>
              <button
                *ngIf="row.paidHasOverride"
                pButton
                type="button"
                icon="pi pi-replay"
                outlined="true"
                pTooltip="Reset"
                tabindex="-1"
                (click)="resetPaid(row.paidKey)"
              ></button>
            </div>
          </td>

          <!-- Unpaid (read-only) -->
          <td>
            <span [class.owed-negative]="row.unpaidIsNegative">
              {{ row.unpaid | currency: vm.currency }}
            </span>
          </td>
        </tr>
      </ng-template>

      <!-- Totals row -->
      <ng-template pTemplate="footer">
        <tr>
          <td class="name-col"><strong>Totals</strong></td>
          <td>{{ vm.tableTotals.fees | currency: vm.currency }}</td>
          <td>{{ vm.tableTotals.bonus | currency: vm.currency }}</td>
          <td>{{ vm.tableTotals.tips | currency: vm.currency }}</td>
          <td>{{ vm.tableTotals.total | currency: vm.currency }}</td>
          <td>{{ vm.tableTotals.paid | currency: vm.currency }}</td>
          <td>{{ vm.tableTotals.unpaid | currency: vm.currency }}</td>
        </tr>
      </ng-template>
    </p-table>
    </div>

    <hr class="crew-table-divider" />

    <!-- Table 3: document-compliance flags (manual checkboxes). Contract For This Job is per
         (job × crew member) and saves with the job; ACH Info / W-9 are per crew member and
         save immediately on toggle. -->
    <div class="doc-flags-table">
    <p-table
      [value]="vm.rows"
      [rowTrackBy]="trackByUserId"
      styleClass="p-datatable-sm"
    >
      <ng-template pTemplate="caption">
        Document compliance — Contract For This Job is per job; ACH Info and W-9 are per crew
        member (they apply to all of that person's jobs) and save immediately.
      </ng-template>
      <ng-template pTemplate="header">
        <tr>
          <th>Name</th>
          <th>Contract For This Job</th>
          <th>ACH Info</th>
          <th>W-9</th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-row>
        <tr>
          <td class="name-col">{{ row.name }}</td>
          <td>
            <div class="crew-pay-cell">
              <p-checkbox
                [ngModel]="row.contractEnabled"
                [binary]="true"
                (onChange)="toggleContract(row.id, !row.contractEnabled)"
              ></p-checkbox>
            </div>
          </td>
          <td>
            <div class="crew-pay-cell">
              <p-checkbox
                [ngModel]="row.achInfo"
                [binary]="true"
                [disabled]="row.achSaving"
                (onChange)="toggleUserFlag(vm.jobId, row.id, row.name, 'ach', !row.achInfo)"
              ></p-checkbox>
              <i *ngIf="row.achSaving" class="pi pi-spin pi-spinner"></i>
            </div>
          </td>
          <td>
            <div class="crew-pay-cell">
              <p-checkbox
                [ngModel]="row.w9"
                [binary]="true"
                [disabled]="row.w9Saving"
                (onChange)="toggleUserFlag(vm.jobId, row.id, row.name, 'w9', !row.w9)"
              ></p-checkbox>
              <i *ngIf="row.w9Saving" class="pi pi-spin pi-spinner"></i>
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>
    </div>
    </ng-container>

  </ng-container>
</div>
